import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FaTag, FaSearch, FaKey, FaUniversity, FaCheckCircle } from 'react-icons/fa';
import { useLang } from '../lib/i18n';
import { contactPath } from '../lib/ru';
import { organizationSchema } from '../lib/jsonLd';
import { ABOUT_META, ABOUT, REVIEWS } from '../lib/about';
import aboutImg from '../assets/about.png';
import '../styles/gradientButton.css';

const SERVICE_ICONS = { sale: FaTag, buy: FaSearch, rent: FaKey, mortgage: FaUniversity };

// "Despre noi" page — bilingual via LangContext (RO route renders bare, RU route via RuWrap).
const About = () => {
    const lang = useLang();
    const ru = lang === 'ru';
    const meta = ABOUT_META;
    const t = ABOUT[ru ? 'ru' : 'ro'];
    const curPath = ru ? meta.ruPath : meta.roPath;

    return (
        <>
            <Helmet>
                <title>{ru ? meta.ruTitle : meta.roTitle}</title>
                <meta name="description" content={ru ? meta.ruDesc : meta.roDesc} />
                <link rel="canonical" href={`https://lvgimob.md${curPath}`} />
                <link rel="alternate" hreflang="ro" href={`https://lvgimob.md${meta.roPath}`} />
                <link rel="alternate" hreflang="ru" href={`https://lvgimob.md${meta.ruPath}`} />
                <link rel="alternate" hreflang="x-default" href={`https://lvgimob.md${meta.roPath}`} />
                <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
            </Helmet>

            {/* Hero / intro */}
            <section className='py-[60px] sm:py-[80px] px-[12px] sm:container sm:mx-auto'>
                <div className="flex flex-col lg:flex-row gap-[40px] items-center">
                    <div className="w-full lg:w-3/5 flex flex-col gap-[20px]">
                        <p className="text-[14px] font-body font-[500] text-[#d5ab63] uppercase tracking-wide">{t.eyebrow}</p>
                        <h1 className="font-[500] font-heading tracking-wide text-[28px] xs:text-[32px] lg:text-[40px]">{ru ? meta.ruH1 : meta.roH1}</h1>
                        <h2 className="font-[500] font-heading tracking-wide text-[20px] xs:text-[24px] lg:text-[28px] text-gray-800">{t.heroTitle}</h2>
                        <p className="font-body text-[14.5px] text-gray-700 leading-[1.7]">{t.heroText}</p>
                        <ul className="flex flex-col gap-[10px] font-body text-[14.5px]">
                            {t.heroFacts.map((f) => (
                                <li key={f.label} className="flex gap-[10px]">
                                    <span className="font-[500] min-w-[95px] text-[#8a5f2b]">{f.label}:</span>
                                    {f.href ? <a href={f.href} className="text-gray-700 hover:text-[#d5ab63] transition-colors duration-100 ease-in">{f.value}</a> : <span className="text-gray-700">{f.value}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-red-100 p-[35px] xs:p-[50px] mx-auto rounded-[5px] w-full lg:w-2/5">
                        <img src={aboutImg} alt={ru ? 'LVG Imob — агентство недвижимости в Унгенах' : 'LVG Imob — agenție imobiliară din Ungheni'} className="w-full" loading="eager" decoding="async" />
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="bg-[#181616] py-[60px] sm:py-[80px] px-[12px]">
                <div className="sm:container sm:mx-auto flex flex-col gap-[30px]">
                    <div className="flex flex-col gap-[10px] items-center text-center">
                        <p className="text-[14px] font-body font-[500] text-[#d5ab63] uppercase tracking-wide">{t.statsEyebrow}</p>
                        <h2 className="font-[500] font-heading text-[26px] xs:text-[30px] lg:text-[36px] text-white">{t.statsTitle}</h2>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-[30px] text-center">
                        {t.stats.map((s) => (
                            <div key={s.value} className="flex flex-col gap-[8px]">
                                <p className="font-heading text-[34px] lg:text-[40px] text-[#d5ab63]">{s.value}</p>
                                <p className="font-body text-[14px] text-gray-300 leading-[1.6]">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-[60px] sm:py-[80px] px-[12px] sm:container sm:mx-auto flex flex-col gap-[30px]">
                <div className="flex flex-col gap-[10px] items-center text-center">
                    <p className="text-[14px] font-body font-[500] text-[#d5ab63] uppercase tracking-wide">{t.servicesEyebrow}</p>
                    <h2 className="font-[500] font-heading text-[28px] xs:text-[32px] lg:text-[40px]">{t.servicesTitle}</h2>
                </div>
                <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 font-body">
                    {t.services.map((s) => {
                        const Icon = SERVICE_ICONS[s.icon];
                        const inner = (
                            <>
                                <div className="bg-[#d5ab63] w-14 h-14 flex items-center justify-center rounded-full mb-4">
                                    <Icon className="text-white text-2xl" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                                <p className="text-sm text-gray-600">{s.text}</p>
                            </>
                        );
                        const cardClass = 'block bg-[#ececec] rounded-[5px] p-8 text-left hover:shadow-lg transition duration-100 ease-in';
                        return s.external ? (
                            <a key={s.title} href={s.href} target="_blank" rel="noopener noreferrer" className={cardClass}>{inner}</a>
                        ) : (
                            <Link key={s.title} to={s.href} className={cardClass}>{inner}</Link>
                        );
                    })}
                </div>
            </section>

            {/* Values */}
            <section className="py-[60px] sm:py-[80px] px-[12px] sm:container sm:mx-auto flex flex-col gap-[30px]">
                <div className="flex flex-col gap-[10px] items-center text-center">
                    <p className="text-[14px] font-body font-[500] text-[#d5ab63] uppercase tracking-wide">{t.valuesEyebrow}</p>
                    <h2 className="font-[500] font-heading text-[28px] xs:text-[32px] lg:text-[40px]">{t.valuesTitle}</h2>
                </div>
                <div className="grid gap-8 md:grid-cols-2">
                    {t.values.map((v) => (
                        <div key={v.title} className="border-[1px] border-gray-300 rounded-[5px] p-[25px] flex flex-row gap-[20px]">
                            <FaCheckCircle className="text-[#d5ab63] text-[24px] mt-[4px] shrink-0" />
                            <div className="flex flex-col gap-[8px]">
                                <p className="font-poppins text-[18px] font-[500]">{v.title}</p>
                                <p className="font-body text-[13.5px] text-gray-700 leading-[1.7]">{v.text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Reviews */}
            <section className="bg-[#f5f2ec] py-[60px] sm:py-[80px] px-[12px]">
                <div className="sm:container sm:mx-auto flex flex-col gap-[30px]">
                    <div className="flex flex-col gap-[10px] items-center text-center">
                        <p className="text-[14px] font-body font-[500] text-[#d5ab63] uppercase tracking-wide">{t.reviewsEyebrow}</p>
                        <h2 className="font-[500] font-heading text-[28px] xs:text-[32px] lg:text-[40px]">{t.reviewsTitle}</h2>
                        <p className="font-body text-[13.5px] text-gray-600 max-w-[600px] leading-[1.6]">{t.reviewsNote}</p>
                    </div>
                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                        {REVIEWS.map((r) => (
                            <figure key={r.name} className="bg-white rounded-[5px] p-8 flex flex-col gap-[16px] shadow-sm">
                                <blockquote className="font-body text-[14.5px] text-gray-700 leading-[1.7]">„{r.text}”</blockquote>
                                <figcaption className="flex flex-col gap-[4px]">
                                    <span className="font-poppins font-[500] text-[15px]">{r.name}</span>
                                    <span className="font-body text-[12.5px] text-[#8a5f2b]">{t.reviewsGoogle}</span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="bg-[#181616] py-[60px] sm:py-[80px] px-[12px]">
                <div className="sm:container sm:mx-auto flex flex-col gap-[30px] items-center text-center">
                    <h2 className="font-[500] font-heading text-[26px] xs:text-[30px] lg:text-[36px] text-white">{t.contactTitle}</h2>
                    <p className="font-body text-[14.5px] text-gray-300 max-w-[600px] leading-[1.7]">{t.contactText}</p>
                    <p className="font-body text-[14.5px] text-gray-300">
                        <a href="tel:+37369003757" className="text-[#d5ab63] hover:text-[#b48a4d] transition-colors duration-100 ease-in">+373 69 003 757</a>
                        {' · '}
                        <a href="mailto:lvgimob1@gmail.com" className="text-[#d5ab63] hover:text-[#b48a4d] transition-colors duration-100 ease-in">lvgimob1@gmail.com</a>
                        {' · '}
                        <span>{ru ? 'ул. Ион Крянгэ 4, Унгены' : 'str. Ion Creangă 4, Ungheni'}</span>
                    </p>
                    <div className="flex flex-col xs:flex-row gap-[14px]">
                        <Link to={contactPath(lang)} className="btn btn-main uppercase text-white font-body text-[14px] font-[500] px-[30px] py-[12px] rounded-[5px] cursor-pointer text-center">
                            {t.contactBtn}
                        </Link>
                        <Link to={ru ? '/ru/cerere' : '/cerere'} className="font-body text-[14px] font-[500] px-[30px] py-[12px] rounded-[5px] border-[1px] border-[#d5ab63] text-[#d5ab63] hover:bg-[#d5ab63] hover:text-white transition-all duration-100 ease-in cursor-pointer text-center">
                            {t.sellBtn}
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
};

export default About;
